import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Pencil, Loader2 } from 'lucide-react';
import { RUBROS, type TipoDeEmpresa } from '../types/admin.types';

/**
 * El rubro de una empresa, y cómo se corrige.
 *
 * 🔴 El hueco que cierra. El rubro se elegía al dar de alta la empresa y después **no
 * había forma de cambiarlo desde ninguna pantalla**: la API lo aceptaba, la interfaz no
 * lo ofrecía. Para corregir una empresa mal clasificada había que llamar a la API a
 * mano.
 *
 * Y hace falta poder corregirlo, no es un lujo: el rubro de un cliente se aclara con el
 * tiempo. El primer día se sabe menos que al mes, y una empresa cargada como Retail que
 * en realidad vende comida se compara contra el mercado equivocado sin que nadie lo note
 * — porque la pantalla igual muestra números.
 *
 * Arranca cerrado, como insignia. Cambiar el rubro no es algo que se haga seguido, así
 * que un desplegable siempre visible en la cabecera invita a tocar lo que casi nunca hay
 * que tocar.
 */
export function RubroDeLaEmpresa({
  rubro,
  onCambiar,
  guardando,
  soloLectura = false,
}: {
  /** Una empresa archivada no se toca: se ve el rubro pero no se ofrece cambiarlo. */
  soloLectura?: boolean;
  rubro: TipoDeEmpresa;
  onCambiar: (nuevo: TipoDeEmpresa) => void;
  guardando?: boolean;
}) {
  const [editando, setEditando] = useState(false);
  const actual = RUBROS[rubro];

  if (!editando) {
    return (
      <div className="mt-1.5 flex items-center gap-2">
        <Badge variant="outline" className={actual?.clases}>
          {actual?.etiqueta ?? rubro}
        </Badge>
        {!soloLectura && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-xs text-muted-foreground"
            onClick={() => setEditando(true)}
            aria-label="Cambiar el rubro de esta empresa"
          >
            <Pencil className="mr-1 h-3 w-3" />
            Cambiar rubro
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-1.5 space-y-2">
      <div className="flex items-center gap-2">
        <Select
          value={rubro}
          disabled={guardando}
          onValueChange={(v) => {
            onCambiar(v as TipoDeEmpresa);
            setEditando(false);
          }}
        >
          <SelectTrigger className="h-8 w-[180px]" aria-label="Rubro de la empresa">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(RUBROS) as TipoDeEmpresa[]).map((k) => (
              <SelectItem key={k} value={k}>
                {RUBROS[k].etiqueta}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {guardando ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => setEditando(false)}
          >
            Cancelar
          </Button>
        )}
      </div>
      {/*
        Se dice qué cambia, porque el nombre del rubro no lo dice. Y se aclara que no
        borra nada: alguien que va a cambiar la clasificación de un cliente en
        producción necesita saber qué consecuencias tiene antes de tocar.
      */}
      <p className="max-w-lg text-xs text-muted-foreground">
        Cambia contra qué se compara esta empresa
        {rubro === 'COMIDA'
          ? ' — hoy, contra restaurantes en aplicaciones de delivery.'
          : ' — hoy, contra tiendas de ecommerce.'}{' '}
        No borra ni modifica ningún dato suyo.
      </p>
    </div>
  );
}
